import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import {
    ContentDataTypes,
    DataLoaderGlobalParams,
    loadContentData,
} from '@news-mono/web-common'
import { CollectionDataContext } from '../../contexts/CollectionDataContext/CollectionDataContext'
import { isEqual } from 'lodash'

// A page size to be used in the event a non-paginated dataDefinition is provided.
const FALLBACK_PAGE_SIZE = 15

/** A hook that provides controls for pagination components. */
export const usePagination = (
    contextId: string,
    services: DataLoaderGlobalParams,
) => {
    const { collectionData, initialDataDefinition, setCollectionData } =
        useContext(CollectionDataContext).forId(contextId)

    const [dataDefinition, setDataDefinition] = useState(initialDataDefinition)

    // Update context stored data when dataDefinition changes.
    useEffect(() => {
        const loadData = async () => {
            const data = await loadContentData(dataDefinition, services)

            setCollectionData((oldCollectionData) => {
                const newData = { loaded: true, result: data }

                // If the collection is different, then we want to set new data
                if (!isEqual(oldCollectionData, newData)) {
                    return newData
                } else {
                    return oldCollectionData
                }
            })
        }

        loadData()

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataDefinition, setCollectionData])

    const paging = useMemo<Paging>(
        () => getPaging(dataDefinition),
        [dataDefinition],
    )

    // A method which sets the page currently displayed by the pagination.
    const setPage = useCallback(
        (page: number) => {
            setDataDefinition((oldDataDefinition) => {
                if (oldDataDefinition.type === 'listing') {
                    const newDataDefinition = {
                        ...oldDataDefinition,
                        paging: {
                            ...oldDataDefinition.paging,
                            page,
                        },
                    }
                    return newDataDefinition
                } else if (oldDataDefinition.type === 'edition') {
                    const newDataDefinition = {
                        ...oldDataDefinition,
                        page,
                    }
                    return newDataDefinition
                }

                // Do not modify if not an edition or listing.
                return oldDataDefinition
            })
        },
        [setDataDefinition],
    )

    return { setPage, collectionData, paging }
}

export type Paging = {
    page: number
    pageSize: number
    pageOffset?: number
}

const getPaging = (dataDefinition: ContentDataTypes): Paging => {
    if (dataDefinition.type === 'listing') {
        return dataDefinition.paging
    } else if (dataDefinition.type === 'edition') {
        return {
            pageSize: dataDefinition.pageSize,
            page: dataDefinition.page,
        }
    }

    return { page: 1, pageSize: FALLBACK_PAGE_SIZE }
}
